import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { StateManager } from '../utilities/stateManager';
import algolia from '../utilities/algolia';
import RequestManager from '../utilities/requestManager';
import { Grid, Card, CardContent, CardMedia, Typography, CircularProgress, Box, Alert, Checkbox, FormControlLabel, FormGroup, Chip, IconButton, InputAdornment, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import constants from '../utilities/constants';
import firebase from '../utilities/firebase';
import { useStackZIndex, ModalStackProvider } from '../utilities/ModalStackContext';
import CustomerSelectList from './CustomerSelectList';
import NewCustomerModal from './NewCustomerModal';
import { SalesCreditEditor } from './SalesCreditDialog';
import { currentUserCredit, normalizeCredits, useSalesTeam, CREDIT_SOURCE } from '../utilities/salesCredits';

export default function AddServiceOrderDialog({ open, onClose, onCreated = null, initialCar = null, serviceOrderId = null, callback = null, initialCustomer = null }) {
  const myZ = useStackZIndex();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [cars, setCars] = React.useState([]);
  const [selectedCar, setSelectedCar] = React.useState(initialCar);
  const [serviceDescription, setServiceDescription] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [subcontractOnly, setSubcontractOnly] = React.useState(false);
  const [subcontractRows, setSubcontractRows] = React.useState([{ name: '', cost: '' }]);
  const [selectedCustomer, setSelectedCustomer] = React.useState(initialCustomer?.id || '');
  const [selectedCustomerLabel, setSelectedCustomerLabel] = React.useState(initialCustomer?.display_name || '');
  const [customerOptions, setCustomerOptions] = React.useState([]);
  const [customerQuery, setCustomerQuery] = React.useState('');
  const [customerResults, setCustomerResults] = React.useState([]);
  const [newCustomerOpen, setNewCustomerOpen] = React.useState(false);
  const [pendingCustomerName, setPendingCustomerName] = React.useState('');
  const customerInputRef = React.useRef(null);
  const [loadingCarData, setLoadingCarData] = React.useState(false);
  const [aiProcessing, setAiProcessing] = React.useState(false);
  const [existingOpenSo, setExistingOpenSo] = React.useState(null);
  const [selectedCarStatus, setSelectedCarStatus] = React.useState(initialCar?.status ?? null);
  const [forceOverride, setForceOverride] = React.useState(false);
  const [includeInEstimate, setIncludeInEstimate] = React.useState(true);
  const [estimateParts, setEstimateParts] = React.useState(false);
  // Who gets paid for this order. Credit belongs to the whole service order, not to a
  // single service, so it is only asked when the order itself is created. Seeded with
  // the creating user when they are on the sales team — a rep opening an SO is usually
  // the one who sold the work — but shown in the form so it is a visible default rather
  // than a silent one. Left empty, the field is not written at all and the order stays
  // uncredited — the rep who sold the car does not inherit it. Adding a service to an
  // existing SO does not touch it; change it from the Sales Credit chip on the order.
  const [salesCredits, setSalesCredits] = React.useState([]);
  const salesTeam = useSalesTeam();

  // A car must NOT be in a blocked status (sold / terminated / holding / admin
  // review) to have a service order created for it. Prefer the authoritative
  // Firestore status; fall back to the selected search hit's status.
  const carIneligible = !serviceOrderId && !!selectedCar
    && !constants.isCarServiceEligible(selectedCarStatus ?? selectedCar?.status);

  // The form is suppressed when a blocking open SO exists (admin-overridable) or
  // the selected car is in an ineligible status (hard block).
  const blocked = (!!existingOpenSo && !serviceOrderId && !forceOverride) || carIneligible;

  // Set initial car when dialog opens
  React.useEffect(() => {
    if (open) {
      if (initialCar) {
        setSelectedCar(initialCar);
        setSearchQuery(`${initialCar.year} ${initialCar.make} ${initialCar.model} ${initialCar.stock}`);
        setCars([initialCar]);
      }
      if (initialCustomer?.id) {
        setSelectedCustomer(initialCustomer.id);
        setSelectedCustomerLabel(initialCustomer.display_name || '');
      }
    } else if (!open) {
      // Reset state when dialog closes
      setSelectedCar(null);
      setSearchQuery('');
      setCars([]);
      setServiceDescription('');
      setSubcontractOnly(false);
      setSubcontractRows([{ name: '', cost: '' }]);
      setSelectedCustomer(initialCustomer?.id || '');
      setSelectedCustomerLabel(initialCustomer?.display_name || '');
      setCustomerOptions([]);
      setCustomerQuery('');
      setCustomerResults([]);
      setNewCustomerOpen(false);
      setPendingCustomerName('');
      setLoadingCarData(false);
      setAiProcessing(false);
      setExistingOpenSo(null);
      setSelectedCarStatus(initialCar?.status ?? null);
      setForceOverride(false);
      setIncludeInEstimate(true);
      setEstimateParts(false);
      setSalesCredits([]);
    }
  }, [open, initialCar, initialCustomer]);

  // Seed the credit once the team list lands. Only on open, only when a new order is
  // being created, and only while nothing is picked, so it never overwrites a choice the
  // user already made.
  React.useEffect(() => {
    if (!open || serviceOrderId || !salesTeam.length) return;
    setSalesCredits(prev => (prev.length ? prev : currentUserCredit(salesTeam)));
  }, [open, serviceOrderId, salesTeam]);

  // Fetch car data and build customer options when car is selected
  React.useEffect(() => {
    const fetchCarData = async () => {
      if (!selectedCar || (!selectedCar.objectID && !selectedCar.stock)) {
        // Only clear car-derived state. The selected customer is independent now
        // (search / consignor / buyer / initialCustomer), so don't wipe it here.
        setCustomerOptions([]);
        setExistingOpenSo(null);
        setSelectedCarStatus(null);
        return;
      }

      setLoadingCarData(true);
      try {
        const db = firebase.firestore();
        const carId = selectedCar.objectID || selectedCar.stock;

        // Check for a blocking open SO on this car. New SO creation is blocked
        // while one exists — the user should open the existing one. SOs in
        // 'payment' (or 'complete') no longer block: a car can take a new SO once
        // the prior one has reached billing. Admins can override regardless.
        const ordersSnap = await db.collection('orders').where('car', '==', carId).get();
        const openOrder = ordersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(o => o.status !== 'complete' && o.status !== 'payment')
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0] || null;
        setExistingOpenSo(openOrder);

        // Fetch car document
        const carDoc = await db.doc(`cars/${carId}`).get();
        const carData = carDoc.exists ? carDoc.data() : {};
        setSelectedCarStatus(carData.status ?? null);
        
        // Fetch deal document for buyer
        const dealDoc = await db.doc(`deals/${carId}`).get();
        const dealData = dealDoc.exists ? dealDoc.data() : {};
        
        // Build customer quick-pick options from the car's consignor and buyer.
        // Names are resolved for display; selecting one sets the customer just
        // like the search does. (Skyway is no longer offered as a customer.)
        const roleIds = [
          carData.consignor ? { value: carData.consignor, role: 'Consignor' } : null,
          dealData.buyer ? { value: dealData.buyer, role: 'Buyer' } : null,
        ].filter(Boolean)
          // Skyway Classics is not a selectable customer.
          .filter(({ value }) => !constants.isSkywayCustomer(value));

        const options = await Promise.all(roleIds.map(async ({ value, role }) => {
          let name = '';
          try {
            const custSnap = await db.doc(`customers/${value}`).get();
            if (custSnap.exists) {
              const c = custSnap.data();
              name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
            }
          } catch (e) { /* fall back to role-only label */ }
          return { value, role, name, label: name ? `${role} — ${name}` : role };
        }));

        setCustomerOptions(options);
      } catch (error) {
        console.error('Error fetching car data:', error);
        StateManager.setAlertAndOpen('Error loading car customer data', 'error');
        setCustomerOptions([]);
      } finally {
        setLoadingCarData(false);
      }
    };

    fetchCarData();
  }, [selectedCar]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setCars([]);
      return;
    }

    try {
      const { results } = await algolia.client.search([
        {
          indexName: 'cars',
          query: query,
          params: {
            hitsPerPage: 10,
            attributesToRetrieve: ['objectID', 'stock', 'year', 'make', 'model', 'thumbnail', 'status']
          }
        }
      ]);

      setCars(results[0].hits);
    } catch (error) {
      console.error('Error searching cars:', error);
      StateManager.setAlertAndOpen('Error searching cars', 'error');
    }
  };

  const pickCustomer = (id, label) => {
    setSelectedCustomer(id);
    setSelectedCustomerLabel(label || '');
    setCustomerQuery('');
    setCustomerResults([]);
  };

  const searchCustomers = async (query) => {
    setCustomerQuery(query);
    if (!query.trim()) {
      setCustomerResults([]);
      return;
    }
    try {
      const index = algolia.client.initIndex('customers');
      const { hits } = await index.search(query);
      const mapped = hits
        // Skyway Classics is not a selectable customer.
        .filter(hit => !constants.isSkywayCustomer(hit.objectID))
        .map(hit => ({
        ...hit,
        label: `${hit.first_name || ''} ${hit.last_name || ''}`.trim() || 'Unnamed',
        action: () => pickCustomer(hit.objectID, `${hit.first_name || ''} ${hit.last_name || ''}`.trim()),
      }));
      setCustomerResults([
        { label: 'Make New Customer', action: () => { setPendingCustomerName(query); setCustomerResults([]); setNewCustomerOpen(true); } },
        ...mapped,
      ]);
    } catch (error) {
      console.error('Error searching customers:', error);
      StateManager.setAlertAndOpen('Error searching customers', 'error');
    }
  };

  const handleCreateServiceOrder = async () => {

    // Subcontract-only line items with something entered (description and/or cost).
    const filledSubcontractRows = subcontractRows.filter(
      (row) => row.name.trim() || String(row.cost).trim()
    );

    if (serviceOrderId) {
      if (!serviceDescription.trim()) {
        StateManager.setAlertAndOpen('Please enter a service description', 'error');
        return;
      }
    } else {
      if (!selectedCar) {
        StateManager.setAlertAndOpen('Please select a car', 'error');
        return;
      }

      if (!selectedCar.objectID && !selectedCar.stock) {
        StateManager.setAlertAndOpen('Please select a car first', 'error');
        return;
      }

      if (carIneligible) {
        StateManager.setAlertAndOpen(
          `A service order can't be created for a car with status "${selectedCarStatus ?? selectedCar?.status}".`,
          'error'
        );
        return;
      }

      if (subcontractOnly) {
        if (filledSubcontractRows.length === 0) {
          StateManager.setAlertAndOpen('Please add at least one subcontract item', 'error');
          return;
        }
        if (filledSubcontractRows.some((row) => !row.name.trim())) {
          StateManager.setAlertAndOpen('Each subcontract item needs a description', 'error');
          return;
        }
      } else if (!serviceDescription.trim()) {
        StateManager.setAlertAndOpen('Please enter a service description', 'error');
        return;
      }

      if (!selectedCustomer) {
        StateManager.setAlertAndOpen('Please select a customer', 'error');
        return;
      }

      // Skyway Classics is not a selectable customer — backstop the picker filters.
      if (constants.isSkywayCustomer(selectedCustomer)) {
        StateManager.setAlertAndOpen('Skyway Classics cannot be selected as the customer', 'error');
        return;
      }
    }

    // Adding a service to an EXISTING SO is optimistic: hand the request off to
    // the parent, which drops in a provisional "Estimating…" row and calls
    // createServices in the background. The user shouldn't sit behind the AI
    // estimate spinner — close the dialog immediately.
    if (serviceOrderId) {
      if (callback) {
        callback({
          description: serviceDescription.trim(),
          // Keep the AI-estimated hours for managers and clock-in (hourly)
          // mechanics. Flat-rate mechanics are paid on line hours, so theirs
          // start at 0 and go through the proposed-hours review.
          removeTime: !(StateManager.isManager() || constants.isClockMechanic(StateManager.mechanicId)),
          includeInEstimate,
          estimateParts,
        });
      }
      onClose();
      return;
    }

    setLoading(true);
    try {
      // New SO creation is optimistic: create the shell (fast, no AI) so we can
      // open the SO immediately, then run the AI service estimate in the
      // background. Subcontract-only SOs have no labor description, so no AI.
      const description = subcontractOnly ? '' : serviceDescription.trim();

      const response = await RequestManager.post({
        function: 'createServiceOrderShell',
        variables: {
          carId: selectedCar.objectID || selectedCar.stock,
          description,
          customer: selectedCustomer,
          force: forceOverride && StateManager.isAdmin(),
          createdBy: StateManager.userID,
          createdByName: StateManager.userName,
          // Only send a credit the user can see in the form. Omitted, the field stays
          // absent and the order is uncredited until somebody sets it.
          salesCredits: normalizeCredits(salesCredits),
          salesCreditsSource: CREDIT_SOURCE.MANUAL,
        }
      });

      if (!response.success) {
        if (response.existingOrderId) {
          StateManager.setAlertAndOpen(response.error || 'Open SO already exists', 'warning');
          StateManager.openServiceOrderModal(response.existingOrderId);
          onClose();
          return;
        }
        throw new Error(response.error || 'Failed to create service order');
      }

      const { orderId } = response;

      // Create the subcontract line items for a subcontract-only SO.
      // Vendor is left as "pending" — the user just enters the work and the
      // price. The syncSubcontractPurchase trigger handles linked purchases.
      if (subcontractOnly) {
        const db = firebase.firestore();
        const batch = db.batch();
        const now = moment().format('YYYY/MM/DD');
        filledSubcontractRows.forEach((row) => {
          const ref = db.collection('subcontracts').doc();
          batch.set(ref, {
            name: row.name.trim(),
            vendor: 'pending',
            cost: parseFloat(row.cost) || 0,
            notes: '',
            status: 'pending',
            order: orderId,
            date: now,
            status_time: now,
          });
        });
        await batch.commit();
      }

      // Open the SO and close the dialog right away. When there's a labor
      // description, the service rows stream in via the modal's realtime
      // listener once the background estimate finishes (the Services tab shows
      // an "AI is generating services…" banner until then).
      if (onCreated) {
        onCreated(orderId);
      } else {
        // Open the new SO in the global modal in place — don't navigate the
        // user away from whatever page they were on.
        StateManager.openServiceOrderModal(orderId);
      }
      onClose();
      StateManager.setAlertAndOpen('Service order created', 'success');

      if (description) {
        RequestManager.post({
          function: 'generateOrderServices',
          variables: {
            serviceOrderId: orderId,
            description,
            includeInEstimate,
            estimateParts,
          },
        }).then((r) => {
          if (!r || !r.success) {
            StateManager.setAlertAndOpen(`Couldn't generate services for ${orderId} — add them manually.`, 'error');
          }
        }).catch((err) => {
          console.error('generateOrderServices failed:', err);
          StateManager.setAlertAndOpen(`Couldn't generate services for ${orderId} — add them manually.`, 'error');
        });
      }
    } catch (error) {
      console.error('Error creating service order:', error);
      StateManager.setAlertAndOpen(error.message || 'Error creating service order', 'error');
    } finally {
      setLoading(false);
      setAiProcessing(false);
    }
  };

  return (
    <ModalStackProvider value={myZ}>
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      sx={{ zIndex: myZ }}
    >
      <DialogTitle>
        {aiProcessing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Sending to AI for estimate...</Typography>
          </Box>
        ) : (
          serviceOrderId ? `Add Service to ${serviceOrderId}` : 'Create New Service Order'
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {!serviceOrderId && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search Cars"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  margin="normal"
                  placeholder="Search by stock number, make, model..."
                  autoComplete='off'
                />
              </Grid>
              
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {cars.map((car) => {
                    const eligible = constants.isCarServiceEligible(car.status);
                    return (
                    <Grid item xs={12} sm={6} md={4} key={car.objectID}>
                      <Card
                        onClick={eligible ? () => {
                          setSelectedCar(car);
                          setSelectedCarStatus(car.status ?? null);
                          setCars([car]);
                          setSearchQuery(`${car.year} ${car.make} ${car.model} ${car.stock}`);
                        } : undefined}
                        sx={{
                          cursor: eligible ? 'pointer' : 'not-allowed',
                          opacity: eligible ? 1 : 0.5,
                          border: selectedCar?.objectID === car.objectID ? '2px solid #1976d2' : 'none'
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="140"
                          image={car.thumbnail || 'https://tools.skywayclassics.com/missing_image.jpeg'}
                          alt={`${car.year} ${car.make} ${car.model}`}
                        />
                        <CardContent>
                          <Typography variant="h6" component="div">
                            {car.stock}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {car.year} {car.make} {car.model}
                          </Typography>
                          {!eligible && (
                            <Chip
                              label={`Not eligible — ${car.status}`}
                              size="small"
                              color="default"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                    );
                  })}
                </Grid>
              </Grid>

              {existingOpenSo && !forceOverride && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    action={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            StateManager.openServiceOrderModal(existingOpenSo.id);
                            onClose();
                          }}
                        >
                          Open {existingOpenSo.id}
                        </Button>
                        {StateManager.isAdmin() && (
                          <Button
                            color="inherit"
                            size="small"
                            variant="outlined"
                            onClick={() => setForceOverride(true)}
                          >
                            Create new SO anyway
                          </Button>
                        )}
                      </Box>
                    }
                  >
                    This car already has an open service order ({existingOpenSo.id}, status: {existingOpenSo.status || 'open'}). Open it instead of creating a new one.
                  </Alert>
                </Grid>
              )}

              {existingOpenSo && forceOverride && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    Admin override: creating a new service order even though {existingOpenSo.id} ({existingOpenSo.status || 'open'}) is still open.
                  </Alert>
                </Grid>
              )}

              {carIneligible && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    A service order can't be created for a car with status "{selectedCarStatus ?? selectedCar?.status}". Only cars that aren't sold, terminated, holding, or in admin review are eligible.
                  </Alert>
                </Grid>
              )}

              {!blocked && (
                <>
                  <Grid item xs={12}>
                    <Box ref={customerInputRef} sx={{ mt: 2 }}>
                      {selectedCustomer ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Customer:</Typography>
                          <Chip
                            label={selectedCustomerLabel || 'Selected customer'}
                            onDelete={loading ? undefined : () => pickCustomer('', '')}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label="Customer"
                          required
                          value={customerQuery}
                          onChange={(e) => searchCustomers(e.target.value)}
                          placeholder="Search customers by name, or make a new one..."
                          disabled={loading}
                          autoComplete="off"
                        />
                      )}
                      {!selectedCustomer && (
                        <CustomerSelectList
                          results={customerResults}
                          selected={-1}
                          onSelect={(item) => item.action?.()}
                          anchorEl={customerInputRef.current}
                        />
                      )}
                      {!selectedCustomer && customerOptions.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {customerOptions.map((option) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              onClick={() => pickCustomer(option.value, option.name || option.role)}
                              variant="outlined"
                              size="small"
                              disabled={loading}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={subcontractOnly}
                          onChange={(e) => setSubcontractOnly(e.target.checked)}
                          disabled={loading}
                        />
                      }
                      label="Subcontract work only (no service shop)"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                      Skips labor services — just enter the subcontracted work and its price. Vendor is set to "pending".
                    </Typography>
                  </Grid>
                </>
              )}
            </>
          )}

          {!blocked && subcontractOnly && !serviceOrderId && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Subcontracted Work</Typography>
              {subcontractRows.map((row, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {index > 0 && <Divider sx={{ mb: 2 }} />}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={row.name}
                        onChange={(e) => setSubcontractRows((prev) => prev.map((r, i) => i === index ? { ...r, name: e.target.value } : r))}
                        disabled={loading}
                        autoFocus={index === 0}
                      />
                    </Grid>
                    <Grid item xs={10} sm={3}>
                      <TextField
                        fullWidth
                        label="Price"
                        type="number"
                        value={row.cost}
                        onChange={(e) => setSubcontractRows((prev) => prev.map((r, i) => i === index ? { ...r, cost: e.target.value } : r))}
                        disabled={loading}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={2} sm={1}>
                      {subcontractRows.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => setSubcontractRows((prev) => prev.filter((_, i) => i !== index))}
                          disabled={loading}
                          aria-label="Remove item"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => setSubcontractRows((prev) => [...prev, { name: '', cost: '' }])}
                disabled={loading}
                sx={{ mt: 1 }}
              >
                Add another item
              </Button>
            </Grid>
          )}

          {!blocked && !subcontractOnly && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Description"
                  multiline
                  rows={4}
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  margin="normal"
                  placeholder="Describe what needs to be fixed..."
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeInEstimate}
                        onChange={(e) => setIncludeInEstimate(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Include in estimate"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={estimateParts}
                        onChange={(e) => setEstimateParts(e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Estimate & add parts for these services"
                  />
                  {estimateParts && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                      Parts will be added as pending with estimated pricing, and follow the same estimate inclusion as the services above.
                    </Typography>
                  )}
                </FormGroup>
              </Grid>
            </>
          )}

          {/* Credit covers the whole order, so it is asked once at creation. Adding a
              service to an existing SO leaves it alone. */}
          {!blocked && !serviceOrderId && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Sales Credit
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Who earns this order. Split it when two reps worked it together, or clear it
                to leave the order uncredited. This is separate from the rep who sold the car.
              </Typography>
              <SalesCreditEditor
                value={salesCredits}
                onChange={setSalesCredits}
                disabled={loading}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        {!blocked && (
          <Button
            onClick={handleCreateServiceOrder}
            variant="contained"
            color="primary"
            disabled={loading || (!serviceOrderId && (!selectedCar || !selectedCustomer || (subcontractOnly
              ? !subcontractRows.some((row) => row.name.trim())
              : !serviceDescription.trim())))}
          >
            {loading ? <CircularProgress size={24} /> : (serviceOrderId ? 'Add Service' : 'Create Service Order')}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <NewCustomerModal
      open={newCustomerOpen}
      onClose={() => setNewCustomerOpen(false)}
      initialName={pendingCustomerName}
      linkToDeal={false}
      heading="New Customer"
      onSaved={(cust) => pickCustomer(cust.id, cust.display_name)}
    />
    </ModalStackProvider>
  );
}